<template>
    <div class="muven-container">
        <div class="muven-router-name-container has-tour">
            <p class="muven-router-name">Detalhes do pedido</p>
            <FeatureTourCmp :tour="tour" :ready="!loading && !loadError" />
        </div>

        <div class="muven-content">
            <div class="modal" tabindex="-1" role="dialog" id="muven-order-xml-modal">
                <div class="modal-dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Arquivo XML da nota fiscal</h5>                            
                        </div>
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-12">
                                    <div class="muven-tip muven-tip-standalone tip-info mb-3">
                                        <fa icon="info" class="tip-icon" />
                                        <p>Ao adicionar o arquivo XML ao faturamento manualmente, as informações de série, número e chave de acesso da nota serão preenchidas automaticamente.</p>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 form-group">
                                    <label>Escolha o arquivo XML</label>
                                    <input type="file" 
                                        id="muven-order-xml-input"
                                        class="form-control" 
                                        accept=".xml"
                                        @change="fileChosenHandler" />
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="addXmlToOrder(order, orderXml)"
                                :disabled="!orderXml.hasXml() || submittingOrderXml">
                                    Adicionar
                                    <Loading v-show="submittingOrderXml" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeOrderXmlModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <Breadcrumb :items="breadcrumbList" class="mb-3"/>

            <Loading page v-if="loading" />            

            <ResourceNotFound
                v-if="!loading && loadError"
                resource="pedido"
                :identifier="$route.params.id"
                list-path="/pedidos"
                list-label="Voltar para pedidos"
                :kind="loadErrorKind"
                :error-message="loadErrorMessage"
                @retry="load" />

            <div class="ps od" v-if="!loading && !loadError">
                <div v-if="imageViewerUrl" class="muven-product-big-image-visualization">
                    <div class="muven-product-big-image-backdrop" @click="imageViewerUrl = ''"></div>
                    <div class="muven-product-big-image-container">
                        <div class="muven-product-big-image">
                            <img :src="imageViewerUrl" alt="Imagem do produto" />
                            <fa icon="times" class="muven-product-big-image-close-icon" @click="imageViewerUrl = ''" />
                        </div>
                    </div>
                </div>

                <header class="ps-hero od-hero" data-tour="order-hero" :style="heroStyle">
                    <BrandTileCmp class="od-hero-tile" :size="64" :image="channelMarkUrl" :icon="isCustomChannel ? 'store-alt' : null"
                        :text="channelBrand.monogram" :background="channelBrand.color" :color="channelBrand.textColor" />

                    <div class="ps-hero-main">
                        <p class="ps-eyebrow">Pedido {{channelPhrase}}</p>
                        <h2 class="ps-hero-title od-hero-title">
                            <span class="od-number">#{{order.idExternalChannel || order.id}}</span>
                            <CopyButtonCmp class="od-title-copy" :value="order.idExternalChannel || order.id" label="número do pedido" />
                        </h2>

                        <div class="ps-hero-chips">
                            <span class="od-status" :class="`is-${statusStyle.tone}`">
                                <fa :icon="statusStyle.icon" />
                                {{order.getOrderStatusEnumDescription()}}
                            </span>
                            <span class="ps-chip"><small>Venda</small><code>{{order.createdAt | dateTimeFilter | infoOrPlaceholder}}</code></span>
                            <span class="ps-chip"><small>Muven</small><code>{{order.id}}</code></span>
                            <span class="ps-chip" v-if="order.store && order.store.idSource"><small>Loja</small><code>{{order.store.idSource}}</code></span>
                        </div>

                        <div class="od-actions">
                            <button type="button" class="od-action"
                                v-if="order.canObtainInfoFromChannel()"
                                :disabled="forcingRetrySync"
                                @click="forceRetrySync(order)">
                                <fa icon="cloud-download-alt" />
                                Obter atualização do canal
                                <Loading v-show="forcingRetrySync" />
                            </button>

                            <button type="button" class="od-action"
                                v-if="order.canForceSync()"
                                :disabled="forcingChannelSync"
                                @click="forceChannelSync(order)">
                                <fa icon="sync-alt" />
                                Forçar atualização no canal
                                <Loading v-show="forcingChannelSync" />
                            </button>

                            <a class="od-action" :href="getAuditUrl(order.id, EntityEnum.Order)" target="_blank" rel="noopener">
                                <fa icon="clipboard-list" />
                                Ir para auditoria
                                <fa icon="external-link-alt" class="od-action-external" />
                            </a>

                            <!-- Canais que nunca informam a entrega: o cliente marca, a partir de 15 dias da venda -->
                            <button type="button" class="od-action"
                                v-if="order.isPosted() && !confirmingDelivered"
                                :disabled="!order.canBeMarkedAsDelivered() || markingDelivered"
                                :title="order.canBeMarkedAsDelivered() ? 'Use quando o canal não informou a entrega' : deliveredMarkHint"
                                @click="confirmingDelivered = true">
                                <fa icon="handshake" />
                                Marcar como entregue
                            </button>
                        </div>

                        <p v-if="order.isPosted() && !order.canBeMarkedAsDelivered()" class="od-action-hint">
                            {{deliveredMarkHint}}
                        </p>

                        <div v-if="confirmingDelivered" class="od-confirm" role="alertdialog" aria-label="Confirmar entrega do pedido">
                            <p>
                                <strong>Confirmar que este pedido foi entregue?</strong>
                                <span>O status passa para Entregue e a mudança fica registrada na auditoria.</span>
                            </p>
                            <div class="od-confirm-actions">
                                <button type="button" class="od-action is-ghost" :disabled="markingDelivered" @click="confirmingDelivered = false">Cancelar</button>
                                <button type="button" class="od-action is-primary" ref="confirmDeliveredButton" :disabled="markingDelivered" @click="markAsDelivered">
                                    <Loading v-if="markingDelivered" />
                                    <template v-else><fa icon="handshake" /> Confirmar entrega</template>
                                </button>
                            </div>
                        </div>
                    </div>

                    <dl class="ps-hero-figures">
                        <div>
                            <dt>Total</dt>
                            <dd>{{order.getTotal() | currency}}</dd>
                        </div>
                        <div>
                            <dt>Unidades</dt>
                            <dd>{{totalUnits}}</dd>
                        </div>
                    </dl>
                </header>

                <!-- Jornada do pedido: só o estágio atual, sem datas que o pedido não traz -->
                <section class="od-journey" data-tour="order-journey" :class="{'has-truck': hasLane}" v-if="statusStyle.step >= 0" aria-label="Andamento do pedido">
                    <div class="od-journey-track">
                        <div v-if="hasLane" class="od-road" :class="`is-${truckMode}`" aria-hidden="true">
                            <!-- Enviado: o caminhão do canal roda sobre a linha da trilha; entregue: faz o trajeto e para no destino -->
                            <div v-if="truckMode" class="od-truck" :class="[`is-${truckMode}`, {'from-transit': truckFromTransit}]" @animationend="onTruckArrived">
                                <OrderTruckCmp :channel="order.channel" :moving="truckMoving" />
                            </div>

                            <!-- Demais etapas: o desenho do que está acontecendo, sobre a bolinha atual -->
                            <div v-if="stageArt" class="od-stage-art" :class="`is-${stageArt}`" :style="{left: `${stageArtLeft}%`}">
                                <OrderStageArtCmp :stage="stageArt" />
                            </div>

                            <!-- Chegada: um brilho rápido na bolinha de Entregue -->
                            <span v-if="showArrivalSparkle" class="od-sparkle">
                                <span v-for="n in 5" :key="n" :style="{'--a': `${n * 72 - 90}deg`}"></span>
                            </span>
                        </div>

                        <ol :style="{'--current': statusStyle.step}">
                            <li v-for="(label, index) in journey" :key="label"
                                :style="{'--i': index}"
                                :class="{'is-done': isStepDone(index), 'is-current': index === statusStyle.step && !isStepDone(index)}"
                                :aria-current="index === statusStyle.step ? 'step' : null">
                                <span class="od-journey-dot">
                                    <fa v-if="isStepDone(index)" icon="check" />
                                    <template v-else>{{index + 1}}</template>
                                </span>
                                <span class="od-journey-label">{{label}}</span>
                            </li>
                        </ol>
                    </div>
                </section>

                <!-- Cancelado: a trilha fica apagada e o cancelamento aparece no meio dela, com a caixa do Muven -->
                <section v-else-if="statusStyle.stage === 'canceled'" class="od-journey has-truck od-canceled" data-tour="order-journey" role="status" aria-label="Pedido cancelado">
                    <div class="od-journey-track">
                        <div class="od-road" aria-hidden="true">
                            <div class="od-stage-art is-canceled" :style="{left: `${canceledCenter}%`}">
                                <OrderStageArtCmp stage="canceled" />
                            </div>
                        </div>

                        <span class="od-canceled-line" aria-hidden="true"></span>
                        <span class="od-canceled-tint" :style="{left: `${canceledCenter}%`}" aria-hidden="true"></span>

                        <ol class="od-canceled-steps">
                            <li v-for="item in canceledJourney" :key="item.key" :class="{'is-cancel': item.cancel}">
                                <template v-if="item.cancel">
                                    <span class="od-cancel-dot">
                                        <svg class="od-cancel-x" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                            <path d="M8 8 L16 16 M16 8 L8 16" />
                                        </svg>
                                    </span>
                                    <span class="od-cancel-title">Pedido cancelado</span>
                                    <span class="od-cancel-text">Este pedido foi cancelado e não seguirá para as próximas etapas.</span>
                                </template>
                                <template v-else>
                                    <span class="od-journey-dot is-off"></span>
                                    <span class="od-journey-label">{{item.label}}</span>
                                </template>
                            </li>
                        </ol>
                    </div>
                </section>

                <section class="od-alert" :class="`is-${statusStyle.stage}`" v-else role="status">
                    <!-- Erro: o aviso balança uma vez -->
                    <fa :icon="statusStyle.icon" class="od-alert-icon" />
                    <div>
                        <p class="od-alert-title">Pedido {{order.getOrderStatusEnumDescription().toLowerCase()}}</p>
                        <p class="od-alert-text">
                            Algo impediu o andamento deste pedido. Veja os detalhes na auditoria.
                        </p>
                    </div>
                </section>

                <div class="ps-grid">
                    <div class="ps-col">
                        <section class="ps-section" data-tour="order-items">
                            <div class="ps-section-head">
                                <h3 class="ps-section-title">Produtos</h3>
                                <span class="ps-count">{{orderItemsDescription}}</span>
                            </div>

                            <div class="od-items">
                                <OrderItemCardCmp
                                    v-for="(item, index) in order.items"
                                    :key="index"
                                    :item="item"
                                    :image-url="itemImage(item).url"
                                    :image-loading="itemImage(item).loading"
                                    @view-image="imageViewerUrl = $event" />
                            </div>

                            <!-- Resumo de valores, como um recibo -->
                            <dl class="od-receipt">
                                <div>
                                    <dt>Subtotal</dt>
                                    <dd>{{order.getItemsTotal() | currency}}</dd>
                                </div>
                                <div v-if="order.discountValue">
                                    <dt>Desconto</dt>
                                    <dd class="is-discount">- {{order.discountValue | currency}}</dd>
                                </div>
                                <div v-if="order.taxesValue">
                                    <dt>Taxa</dt>
                                    <dd>{{order.taxesValue | currency}}</dd>
                                </div>
                                <div v-if="order.incrementValue">
                                    <dt>Acréscimo</dt>
                                    <dd>{{order.incrementValue | currency}}</dd>
                                </div>
                                <div>
                                    <dt>Frete</dt>
                                    <dd>{{order.getFreightTotal() | currency}}</dd>
                                </div>
                                <div class="is-total">
                                    <dt>Total</dt>
                                    <dd>{{order.getTotal() | currency}}</dd>
                                </div>
                            </dl>
                        </section>

                        <section class="ps-section">
                            <div class="ps-section-head">
                                <h3 class="ps-section-title">Faturamento</h3>
                                <span class="ps-count" v-if="order.invoices.length">{{order.invoices.length}}</span>
                            </div>

                            <p v-if="!order.invoices.length" class="ps-empty">Nenhuma nota fiscal neste pedido ainda.</p>

                            <div v-for="(orderInvoice, index) in order.invoices" :key="index" class="od-invoice">
                                <dl class="ps-facts">
                                    <div>
                                        <dt>Número</dt>
                                        <dd>{{orderInvoice.invoiceNumber | infoOrPlaceholder}}</dd>
                                    </div>
                                    <div>
                                        <dt>Série</dt>
                                        <dd>{{orderInvoice.invoiceSeries | infoOrPlaceholder}}</dd>
                                    </div>
                                    <div class="is-wide">
                                        <dt>Chave de acesso</dt>
                                        <dd class="od-key">
                                            <template v-if="orderInvoice.accessKey">
                                                <code>{{formatAccessKey(orderInvoice.accessKey)}}</code>
                                                <CopyButtonCmp :value="orderInvoice.accessKey" label="chave de acesso" />
                                            </template>
                                            <span v-else>-</span>
                                        </dd>
                                    </div>
                                </dl>

                                <div class="od-invoice-actions" v-if="orderInvoice.hasXmlUrl() || order.canAddXml()">
                                    <button type="button" class="od-action"
                                        :disabled="orderInvoice.loadingListAction"
                                        v-show="orderInvoice.hasXmlUrl()"
                                        @click="downloadXml(orderInvoice)">
                                        <fa icon="file-download" v-show="!orderInvoice.loadingListAction" />
                                        <Loading v-show="orderInvoice.loadingListAction" />
                                        Baixar XML
                                    </button>

                                    <button type="button" class="od-action"
                                        v-show="order.canAddXml()"
                                        @click="openOrderXmlModal(orderInvoice)">
                                        <fa icon="plus" />
                                        Adicionar XML
                                    </button>
                                </div>
                            </div>
                        </section>

                        <section class="ps-section" v-if="order.hasSellerNotes()">
                            <h3 class="ps-section-title">Observações</h3>
                            <div class="od-notes">
                                <p v-for="(sellerNote, index) in order.getSellerNotesAsList()" :key="index">
                                    {{sellerNote | infoOrPlaceholder}}
                                </p>
                            </div>
                        </section>
                    </div>

                    <aside class="ps-col">
                        <section class="ps-section" data-tour="order-customer">
                            <h3 class="ps-section-title">Cliente</h3>

                            <p class="od-person">
                                <span class="od-avatar" aria-hidden="true">{{customerInitials}}</span>
                                <span>
                                    <strong>{{customerName}}</strong>
                                    <small v-if="order.customer.document" class="od-document">
                                        {{documentLabel}} {{order.customer.document}}
                                        <CopyButtonCmp :value="order.customer.document" :label="documentLabel" />
                                    </small>
                                </span>
                            </p>

                            <ul class="od-contacts">
                                <li v-if="order.customer.email">
                                    <fa icon="envelope" />
                                    <a :href="`mailto:${order.customer.email}`">{{order.customer.email}}</a>
                                    <CopyButtonCmp :value="order.customer.email" label="e-mail" />
                                </li>
                                <li v-if="customerPhone">
                                    <fa icon="phone" />
                                    <a :href="`tel:${customerPhone}`">{{customerPhone}}</a>
                                    <CopyButtonCmp :value="customerPhone" label="telefone" />
                                </li>
                                <li v-if="order.customer.isLegal() && order.customer.hasNaturalInfo()">
                                    <fa icon="user" />
                                    <span>{{order.customer.fullName()}}</span>
                                </li>
                            </ul>
                        </section>

                        <section class="ps-section">
                            <h3 class="ps-section-title">Endereços</h3>

                            <div class="od-addr-block">
                                <div class="od-addr-head">
                                    <span class="od-addr-kind"><fa icon="truck-loading" /> Entrega</span>
                                    <CopyButtonCmp v-if="shippingText" :value="shippingText" label="endereço de entrega" text="Copiar" />
                                </div>
                                <address class="od-address">
                                    <strong v-if="shipping.receiverName">{{shipping.receiverName}}</strong>
                                    <span>{{shipping.street}}<template v-if="shipping.number">, {{shipping.number}}</template></span>
                                    <span v-if="shipping.complement">{{shipping.complement}}</span>
                                    <span>
                                        <template v-if="shipping.neighborhood">{{shipping.neighborhood}} · </template>
                                        {{shipping.city}}<template v-if="shipping.state">/{{shipping.state}}</template>
                                    </span>
                                    <span v-if="shipping.postalCode" class="od-cep">
                                        CEP <span class="od-cep-num">{{formatPostalCode(shipping.postalCode)}}</span>
                                        <CopyButtonCmp :value="postalCodeDigits(shipping.postalCode) || shipping.postalCode" label="CEP de entrega" />
                                    </span>
                                    <span v-if="shipping.reference" class="od-reference">Ref.: {{shipping.reference}}</span>
                                </address>
                            </div>

                            <div class="od-addr-block" :class="{'is-linked': billingSameAsShipping}">
                                <div class="od-addr-head">
                                    <span class="od-addr-kind"><fa icon="credit-card" /> Cobrança</span>
                                    <CopyButtonCmp v-if="!billingEmpty && billingText && (!billingSameAsShipping || billingExpanded)"
                                        :value="billingText" label="endereço de cobrança" text="Copiar" />
                                </div>
                                <p v-if="billingEmpty" class="ps-empty">Não informado.</p>

                                <template v-else-if="billingSameAsShipping">
                                    <div class="od-addr-same">
                                        <span class="od-addr-same-icon" aria-hidden="true"><fa icon="link" /></span>
                                        <span class="od-addr-same-text">
                                            <strong>Mesmo endereço da entrega</strong>
                                            <span v-if="!billingSameComplement && billing.complement">
                                                Complemento: {{billing.complement}}
                                            </span>
                                        </span>
                                        <button type="button" class="od-addr-toggle" :aria-expanded="billingExpanded ? 'true' : 'false'"
                                            aria-controls="od-billing-address" @click="billingExpanded = !billingExpanded">
                                            {{billingExpanded ? 'Ocultar' : 'Ver'}}
                                            <fa icon="chevron-down" :class="{'is-open': billingExpanded}" />
                                        </button>
                                    </div>
                                    <address v-show="billingExpanded" id="od-billing-address" class="od-address is-reveal">
                                        <strong v-if="billing.receiverName">{{billing.receiverName}}</strong>
                                        <span>{{billing.street}}<template v-if="billing.number">, {{billing.number}}</template></span>
                                        <span v-if="billing.complement">{{billing.complement}}</span>
                                        <span>
                                            <template v-if="billing.neighborhood">{{billing.neighborhood}} · </template>
                                            {{billing.city}}<template v-if="billing.state">/{{billing.state}}</template>
                                        </span>
                                        <span v-if="billing.postalCode" class="od-cep">
                                            CEP <span class="od-cep-num">{{formatPostalCode(billing.postalCode)}}</span>
                                            <CopyButtonCmp :value="postalCodeDigits(billing.postalCode) || billing.postalCode" label="CEP de cobrança" />
                                        </span>
                                    </address>
                                </template>

                                <address v-else class="od-address">
                                    <strong v-if="billing.receiverName">{{billing.receiverName}}</strong>
                                    <span>{{billing.street}}<template v-if="billing.number">, {{billing.number}}</template></span>
                                    <span v-if="billing.complement">{{billing.complement}}</span>
                                    <span>
                                        <template v-if="billing.neighborhood">{{billing.neighborhood}} · </template>
                                        {{billing.city}}<template v-if="billing.state">/{{billing.state}}</template>
                                    </span>
                                    <span v-if="billing.postalCode" class="od-cep">
                                        CEP <span class="od-cep-num">{{formatPostalCode(billing.postalCode)}}</span>
                                        <CopyButtonCmp :value="postalCodeDigits(billing.postalCode) || billing.postalCode" label="CEP de cobrança" />
                                    </span>
                                </address>
                            </div>
                        </section>

                        <section class="ps-section">
                            <h3 class="ps-section-title">Envio</h3>

                            <dl class="ps-facts is-stacked">
                                <div>
                                    <dt>Transportadora</dt>
                                    <dd>{{order.getDeliveryCompany().name | infoOrPlaceholder}}</dd>
                                </div>
                                <div>
                                    <dt>Frete</dt>
                                    <dd>{{order.getFreightTotal() | currency}}</dd>
                                </div>
                                <div>
                                    <dt>Código de rastreio</dt>
                                    <dd>
                                        <div v-if="!editingTracking" class="od-tracking">
                                            <code v-if="order.getInvoice().trackingCode">{{order.getInvoice().trackingCode}}</code>
                                            <span v-else class="ps-empty">Sem código</span>

                                            <button type="button" class="od-icon-btn" title="Atualizar código de rastreio" @click="startTrackingEdit">
                                                <fa icon="edit" />
                                                <span class="sr-only">Atualizar código de rastreio</span>
                                            </button>
                                            <CopyButtonCmp v-if="order.getInvoice().trackingCode" :value="order.getInvoice().trackingCode" label="código de rastreio" />
                                        </div>

                                        <form v-else class="od-tracking-form" @submit.prevent="saveTracking" @keydown.esc.prevent="editingTracking = false">
                                            <input ref="trackingInput" v-model.trim="trackingDraft" type="text" class="od-input"
                                                aria-label="Novo código de rastreio" spellcheck="false" autocomplete="off" :disabled="savingTracking" />
                                            <button type="submit" class="od-action is-primary" :disabled="savingTracking">
                                                <Loading v-if="savingTracking" />
                                                <template v-else>Salvar</template>
                                            </button>
                                            <button type="button" class="od-action is-ghost" :disabled="savingTracking" @click="editingTracking = false">Cancelar</button>
                                        </form>
                                    </dd>
                                </div>
                                <div v-if="order.getInvoice().hasTrackingUrl()">
                                    <dt>Rastreamento</dt>
                                    <dd>
                                        <a class="od-link" :href="order.getInvoice().trackingUrl" target="_blank" rel="noopener">
                                            Acompanhar entrega <fa icon="external-link-alt" />
                                        </a>
                                    </dd>
                                </div>
                            </dl>
                        </section>


                        <section class="ps-section">
                            <h3 class="ps-section-title">Pagamento</h3>

                            <p v-if="!order.payments.length" class="ps-empty">Sem informações de pagamento.</p>

                            <div v-for="(orderPayment, index) in order.payments" :key="index" class="od-payment">
                                <p class="od-payment-head">
                                    <span class="od-payment-icon" aria-hidden="true"><fa icon="credit-card" /></span>
                                    <strong>{{orderPayment.payment.name | infoOrPlaceholder}}</strong>
                                    <span class="od-payment-value">{{orderPayment.value | currency}}</span>
                                </p>
                                <dl class="od-payment-facts">
                                    <div v-if="orderPayment.hasInstallments()">
                                        <dt>Parcelas</dt>
                                        <dd>{{orderPayment.installments}}x</dd>
                                    </div>
                                    <div v-if="orderPayment.hasNsuCode()">
                                        <dt>NSU</dt>
                                        <dd class="od-id"><code>{{orderPayment.nsuCode}}</code><CopyButtonCmp :value="orderPayment.nsuCode" label="NSU" /></dd>
                                    </div>
                                    <div v-if="orderPayment.hasAuthorizationNumber()">
                                        <dt>Autorização</dt>
                                        <dd class="od-id"><code>{{orderPayment.authorizationNumber}}</code><CopyButtonCmp :value="orderPayment.authorizationNumber" label="código de autorização" /></dd>
                                    </div>
                                    <div v-if="orderPayment.hasDetails()" class="is-wide">
                                        <dt>Detalhes</dt>
                                        <dd>{{orderPayment.getDetails()}}</dd>
                                    </div>
                                </dl>
                            </div>
                        </section>

                        <section class="ps-section">
                            <h3 class="ps-section-title">Integração</h3>

                            <div class="od-channel">
                                <ChannelLogoCmp :channel="order.channel" :size="28" />
                            </div>

                            <dl class="ps-facts is-stacked">
                                <div v-for="fact in integrationIds" :key="fact.label">
                                    <dt>{{fact.label}}</dt>
                                    <dd class="od-id">
                                        <code v-if="fact.value">{{fact.value}}</code>
                                        <span v-else>-</span>
                                        <CopyButtonCmp v-if="fact.value" :value="fact.value" :label="fact.label" />
                                    </dd>
                                </div>
                                <div>
                                    <dt>Momento da venda</dt>
                                    <dd>{{order.createdAt | dateTimeFilter | infoOrPlaceholder}}</dd>
                                </div>
                                <div>
                                    <dt>Modificado por</dt>
                                    <dd>{{order.getIntegrationSourceEnumDescription() | infoOrPlaceholder}}</dd>
                                </div>
                            </dl>
                        </section>
                    </aside>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { mapState } from 'vuex'
import {dateTimeFilter, dateFilter} from '@/utils/filters/DateTimeFilter'
import {infoOrPlaceholder} from '@/utils/filters/InformationOrPlaceholderFilter'
import {OrderModel, DAYS_TO_MARK_AS_DELIVERED} from '@/order/models/OrderModel'
import {OrderXmlModel} from '@/order/models/OrderXmlModel'
import orderService from '@/order/services/OrderService'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '@/utils/mixins/GetAuditUrl'
import productService from '@/product/services/ProductService'
import { fileToString } from '../../utils/helpers/FileHelper'
import ModalService from '../../utils/services/ModalService'
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'
import { ChannelEnum } from '@c2b/muven-commons'
import { getChannelBrand, getChannelGlow } from '@/channel/models/ChannelBrand'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp.vue'
import OrderItemCardCmp from '@/order/components/OrderItemCardCmp.vue'
import OrderTruckCmp from '@/order/components/OrderTruckCmp.vue'
import OrderStageArtCmp from '@/order/components/OrderStageArtCmp.vue'
import { OrderTrackingCodeModel } from '@/order/models/OrderTrackingCodeModel'
import { getOrderStatusStyle, ORDER_JOURNEY } from '@/order/utils/OrderStatusStyle'
import { formatPostalCode, postalCodeDigits, isSameLocation, hasSameComplement } from '@/order/utils/AddressHelper'
import CopyButtonCmp from '@/utils/components/CopyButtonCmp.vue'
import FeatureTourCmp from '@/utils/components/FeatureTourCmp.vue'
import { ORDER_DETAIL_TOUR } from '@/utils/tours/FeatureTours'

const ID_ORDER_XML_MODAL = '#muven-order-xml-modal'

// Endereço em linhas, como se escreve numa etiqueta
function formatAddress(address, withReference) {
    if (!address) {
        return ''
    }

    const street = [address.street, address.number].filter((v) => !!v).join(', ')
    const city = [address.city, address.state].filter((v) => !!v).join('/')
    const lines = [
        address.receiverName,
        street,
        address.complement,
        [address.neighborhood, city].filter((v) => !!v).join(' - '),
        address.postalCode ? `CEP ${formatPostalCode(address.postalCode)}` : '',
        withReference && address.reference ? `Ref.: ${address.reference}` : '',
    ]

    return lines.filter((l) => !!l && String(l).trim()).join('\n')
}

function mainImageUrl(product) {
    const images = (product && product.getImages && product.getImages()) || []
    const main = images.find((img) => img.isMain && img.isActive) || images.find((img) => img.isActive)

    return main ? main.url : ''
}

export default {
    name: 'OrderView',    
    components: {
        BrandTileCmp,
        ChannelLogoCmp,
        OrderItemCardCmp,
        OrderTruckCmp,
        OrderStageArtCmp,
        CopyButtonCmp,
        FeatureTourCmp,
    },
    data() {
        return {
            tour: ORDER_DETAIL_TOUR,
            EntityEnum,
            order: new OrderModel(),
            orderXml: new OrderXmlModel(),
            loading: true,
            forcingRetrySync: false,
            forcingChannelSync: false,
            submittingOrderXml: false,
            journey: ORDER_JOURNEY,
            // Imagem de cada produto do pedido, por id: {loading, url}
            itemImages: {},
            imageViewerUrl: '',
            editingTracking: false,
            trackingDraft: '',
            savingTracking: false,
            // Rodas e fumaça: sempre no "enviado"; no "entregue" só durante o trajeto
            truckArrived: false,
            truckDrove: false,
            confirmingDelivered: false,
            // Marcado como entregue aqui: o caminhão parte de onde estava (meio do caminho), não de Enviado
            truckFromTransit: false,
            markingDelivered: false,
            // Cobrança igual à entrega fica recolhida; o usuário abre se precisar conferir
            billingExpanded: false,
            breadcrumbList: [
                {
                    id: 0,
                    description: 'Pedidos',
                    path: '/pedidos',
                },
                {
                    id: 1,
                    description: `Detalhes de #${this.$route.params.id}`,
                    path: '',
                },
            ],
        }
    },
    filters: {
        dateTimeFilter,
        infoOrPlaceholder,
    },
    mixins: [
        GetAuditUrl,
        ResourceLoadError,
    ],
    methods: {
        formatPostalCode,
        postalCodeDigits,
        async load() {
            this.loading = true
            this.loadError = null

            try {
                this.order = await this._getCompleteOrder()
                this.loadItemImages()
            } catch(e) {
                this.loadError = e
            } finally {
                this.loading = false
            }
        },
        /**
         * O pedido não traz imagens: busca cada produto distinto em paralelo,
         * sem segurar a tela. Sem imagem no SKU, usa a do produto pai.
         */
        async loadItemImages() {
            const ids = [...new Set(this.order.items.map((item) => item.product && item.product.id).filter((id) => !!id))]

            ids.forEach((id) => this.$set(this.itemImages, id, {loading: true, url: ''}))

            await Promise.all(ids.map(async (id) => {
                let url = ''

                try {
                    const product = await productService.getProductById(id)
                    url = mainImageUrl(product)

                    if (!url && product.parent && product.parent.id) {
                        url = mainImageUrl(await productService.getProductById(product.parent.id))
                    }
                } catch (e) {
                    // Sem imagem não impede nada: fica o ícone no lugar
                }

                this.$set(this.itemImages, id, {loading: false, url})
            }))
        },
        // Entregue é a última etapa: chegando nela, a jornada está completa
        isStepDone(index) {
            const last = this.journey.length - 1

            return index < this.statusStyle.step || (this.statusStyle.step === last && index === last)
        },
        // Mesmo endpoint do "Andamento do pedido"; o backend grava a auditoria da troca de status
        async markAsDelivered() {
            this.markingDelivered = true

            try {
                await orderService.setStatusToDelivered(this.order.id)
                this.$floatingAlert.success({
                    message: 'Pedido marcado como entregue.',
                })
                this.confirmingDelivered = false
                this.truckFromTransit = this.truckMode === 'transit'
                // Com o status novo, a jornada vira "Entregue" e o caminhão segue até o fim
                this.order = await this._getCompleteOrder()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.markingDelivered = false
        },
        onTruckArrived(event) {
            // Só o fim do trajeto (as animações internas do caminhão também disparam animationend)
            if (event.animationName && event.animationName.indexOf('od-truck-drive') > -1) {
                this.truckDrove = true
                this.truckArrived = true
            }
        },
        itemImage(item) {
            return (item.product && this.itemImages[item.product.id]) || {loading: false, url: ''}
        },
        formatAccessKey(key) {
            return String(key || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
        },
        startTrackingEdit() {
            this.trackingDraft = this.order.getInvoice().trackingCode || ''
            this.editingTracking = true
            this.$nextTick(() => {
                if (this.$refs.trackingInput) {
                    this.$refs.trackingInput.focus()
                    this.$refs.trackingInput.select()
                }
            })
        },
        // Mesmo endpoint de "Atualizar código de rastreio" da listagem
        async saveTracking() {
            this.savingTracking = true

            try {
                await orderService.updateTrackingCode(this.order.id, new OrderTrackingCodeModel({trackingCode: this.trackingDraft}))
                this.$floatingAlert.success({
                    message: 'Código de rastreio atualizado com sucesso!',
                })
                this.editingTracking = false
                this.order = await this._getCompleteOrder()
                this.loadItemImages()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.savingTracking = false
        },
        async downloadXml(invoice) {
            invoice.loadingListAction = true

            try {
                const xml = await orderService.getOrderInvoiceXml(invoice)
                const pom = document.createElement('a')
                const blob = new Blob([xml], {type: 'text/plain'})

                pom.setAttribute('href', window.URL.createObjectURL(blob));
                pom.setAttribute('download', `${invoice.accessKey}.xml`);
                pom.dataset.downloadurl = ['text/plain', pom.download, pom.href].join(':');
                pom.click();
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message
                })
            }

            invoice.loadingListAction = false
        },
        async forceRetrySync(order) {
            this.forcingRetrySync = true

            try {
                const info = await orderService.retrySync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.forcingRetrySync = false
        },
        async forceChannelSync(order) {
            this.forcingChannelSync = true

            try {
                const info = await orderService.forceChannelSync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.forcingChannelSync = false
        },
        async addXmlToOrder(order, orderXml) {
            this.submittingOrderXml = true

            try {
                await orderService.addXml(order.id, orderXml)
                this.order = await this._getCompleteOrder() 
                this.closeOrderXmlModal()

                this.$floatingAlert.success({
                    message: 'XML da nota adicionado com sucesso ao pedido.',
                })                
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.submittingOrderXml = false
        },
        openOrderXmlModal(orderInvoice) {
            document.getElementById('muven-order-xml-input').value = null

            this.orderXml = new OrderXmlModel({
                invoiceId: orderInvoice.id,
                xml: '',
            })

            ModalService.show(ID_ORDER_XML_MODAL)
        },
        closeOrderXmlModal() {
            ModalService.hide(ID_ORDER_XML_MODAL)
        },
        async fileChosenHandler({target}) {
            try {
                this.orderXml.setXml(await fileToString(target.files[0]));
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }
        },
        async _getCompleteOrder() {
            const order = await orderService.getOrderById(this.$route.params.id)

            for (const item of order.items) {
                if (item.product.isSku) {
                    item.product = await productService.getProductById(item.product.id)
                }
            }

            return order
        }
    },
    mounted() {
        this.load()
    },
    watch: {
        // Garante que o caminhão pare mesmo sem a animação do trajeto (celular, movimento reduzido)
        truckMode(mode) {
            if (mode === 'delivered') {
                this.truckArrived = false
                setTimeout(() => {
                    this.truckArrived = true
                }, 3500)
            }
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        deliveredMarkHint() {
            const availableAt = this.order.deliveredMarkAvailableAt()

            return availableAt
                ? `"Marcar como entregue" fica disponível a partir de ${dateFilter(availableAt)} (${DAYS_TO_MARK_AS_DELIVERED} dias após a venda).`
                : ''
        },
        statusStyle() {
            return getOrderStatusStyle(this.order)
        },
        // 'transit': parado no meio do caminho, andando; 'delivered': faz o trajeto até Entregue
        truckMode() {
            if (this.statusStyle.step === 3) {
                return 'transit'
            }

            return this.statusStyle.step === 4 ? 'delivered' : ''
        },
        // Desenho da etapa para quem ainda não saiu (o caminhão cuida de Enviado e Entregue)
        // Trilha do cancelado: o marco de cancelamento entra entre Aprovado e Faturado, mais largo que as etapas
        canceledJourney() {
            const steps = this.journey.map((label) => ({key: label, label}))

            return [...steps.slice(0, 2), {key: 'cancel', cancel: true}, ...steps.slice(2)]
        },
        // Centro do marco na grade (colunas 1 1 2 1 1 1): (2 + 1) / 7
        canceledCenter() {
            return 300 / 7
        },
        stageArt() {
            const stage = this.statusStyle.stage

            return ['pending', 'integrating', 'pendingAssignment', 'approved', 'billed', 'finalized'].includes(stage) ? stage : ''
        },
        // Centro da bolinha da etapa na grade de 5 colunas; a bandeira do Finalizado vai em Entregue
        stageArtLeft() {
            return (Math.max(this.statusStyle.step, 0) + .5) * (100 / this.journey.length)
        },
        hasLane() {
            return !!this.truckMode || !!this.stageArt
        },
        // Só quando o trajeto de fato acontece (em telas em que o caminhão não viaja não tem chegada)
        showArrivalSparkle() {
            return this.statusStyle.stage === 'delivered' && this.truckArrived && this.truckDrove
        },
        truckMoving() {
            return this.truckMode === 'transit' || (this.truckMode === 'delivered' && !this.truckArrived)
        },
        channelBrand() {
            return getChannelBrand(this.order.channel && this.order.channel.enumChannel)
        },
        channelMarkUrl() {
            return this.channelBrand.mark ? require(`@/channel/images/marks/${this.channelBrand.mark}`) : null
        },
        isCustomChannel() {
            return !!this.order.channel && this.order.channel.enumChannel === ChannelEnum.Custom
        },
        // "no canal X" serve para qualquer nome, sem errar o gênero
        channelPhrase() {
            return this.order.channel && this.order.channel.name ? `no canal ${this.order.channel.name}` : ''
        },
        heroStyle() {
            const glow = getChannelGlow(this.channelBrand)

            return glow ? {'--od-glow': glow} : {}
        },
        totalUnits() {
            return this.order.items.reduce((acc, item) => acc + (Number(item.amount) || 0), 0)
        },
        shipping() {
            return this.order.getShippingAddress()
        },
        billing() {
            return this.order.getBillingAddress()
        },
        billingSameAsShipping() {
            return isSameLocation(this.shipping, this.billing)
        },
        billingSameComplement() {
            return hasSameComplement(this.shipping, this.billing)
        },
        documentLabel() {
            return this.order.customer.isLegal() ? 'CNPJ' : 'CPF'
        },
        shippingText() {
            return formatAddress(this.shipping, true)
        },
        billingText() {
            return formatAddress(this.billing, false)
        },
        billingEmpty() {
            return !this.billing.street && !this.billing.postalCode && !this.billing.city
        },
        customerName() {
            const customer = this.order.customer

            if (customer.isLegal() && customer.corporateName) {
                return customer.corporateName
            }

            return customer.fullName() || customer.corporateName || 'Cliente sem nome'
        },
        customerInitials() {
            const words = this.customerName.split(/\s+/).filter((w) => !!w)

            return ((words[0] || '')[0] || '').concat((words.length > 1 ? words[words.length - 1][0] : '') || '').toUpperCase()
        },
        customerPhone() {
            const customer = this.order.customer

            return customer.isLegal() ? (customer.corporatePhone || customer.phone) : customer.phone
        },
        integrationIds() {
            return [
                {label: 'ID no canal', value: this.order.idExternalChannel},
                {label: 'ID na retaguarda', value: this.order.idSource},
                {label: 'Cód. retaguarda', value: this.order.codeSource},
                {label: 'ID Muven', value: this.order.id},
            ]
        },
        orderItemsDescription() {
            if (this.order.items.length > 1) {
                return `${this.order.items.length} itens`
            }

            return `1 item`
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/product/styles/ProductDetail.scss';

// O pedido usa o mesmo idioma visual do detalhe do produto (ps-*); aqui só o que é dele
.od-hero {
    --od-glow: var(--brand);

    align-items: flex-start;

    &::before {
        background: radial-gradient(closest-side, color-mix(in srgb, var(--od-glow) 16%, transparent), transparent);
    }
}

.od-hero-tile {
    position: relative;
    box-shadow: var(--shadow-sm);
}

.od-title-copy {
    margin-left: 6px;
}

.od-document {
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

.od-number {
    font-variant-numeric: tabular-nums;
    word-break: break-all;
}

.od-status {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 3px 11px;
    border: 1px solid var(--border);
    border-radius: 999px;
    color: var(--text-muted);
    background: var(--surface-3);
    font-size: 13px;
    font-weight: 700;

    svg {
        font-size: 12px;
    }

    // Andamento normal do pedido na cor da marca
    &.is-success {
        color: var(--brand);
        background: var(--tint-brand-bg);
        border-color: var(--tint-brand-border);
    }

    &.is-danger {
        color: var(--danger-fg);
        background: var(--danger-bg);
        border-color: var(--danger-border);
    }

    &.is-warning {
        color: var(--warning-fg);
        background: var(--warning-bg);
        border-color: var(--warning-border);
    }

    &.is-info {
        color: var(--info-fg);
        background: var(--info-bg);
        border-color: var(--info-border);
    }
}

.od-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
}

.od-action {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: 9px;
    color: var(--text-strong);
    background: var(--surface-1);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color .15s, border-color .15s, color .15s;

    svg {
        color: var(--text-muted);
        font-size: 12px;
    }

    &:hover:not(:disabled) {
        border-color: var(--tint-brand-border);
        color: var(--brand);
        background: var(--tint-brand-soft);
        text-decoration: none;

        svg {
            color: var(--brand);
        }
    }

    &:disabled {
        opacity: .5;
        cursor: not-allowed;
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px var(--focus-ring);
        outline: none;
    }

    &.is-primary {
        border-color: var(--brand-solid);
        color: var(--text-on-brand);
        background: var(--brand-solid);

        &:hover:not(:disabled) {
            color: var(--text-on-brand);
            background: var(--brand-fill);
        }
    }

    &.is-ghost {
        border-color: transparent;
        color: var(--text-muted);
        background: transparent;
    }
}

.od-action-hint {
    margin: 8px 0 0;
    color: var(--text-muted);
    font-size: 12px;
}

.od-confirm {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 16px;
    margin-top: 12px;
    padding: 12px 14px;
    border: 1px solid var(--tint-brand-border);
    border-radius: 12px;
    background: var(--tint-brand-soft);
    animation: ps-rise .3s cubic-bezier(.22, 1, .36, 1) both;

    p {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin: 0;
        font-size: 13px;
    }

    strong {
        color: var(--text-strong);
        font-size: 14px;
    }

    span {
        color: var(--text-muted);
    }
}

.od-confirm-actions {
    display: flex;
    gap: 8px;
}

.od-action-external {
    font-size: 10px !important;
    opacity: .7;
}

// Jornada do status: as etapas cumpridas ficam ligadas por uma linha cheia
.od-journey {
    margin-bottom: 18px;
    padding: 16px 22px;
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    background: var(--surface-1);
    animation: ps-rise .5s .05s $ease-out both;

    ol {
        display: flex;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    li {
        position: relative;
        display: flex;
        flex: 1;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        color: var(--text-faint);
        font-size: 12px;
        font-weight: 700;
        text-align: center;

        // Trilho até a próxima etapa
        &:not(:last-child)::after {
            content: '';
            position: absolute;
            top: 13px;
            left: calc(50% + 18px);
            right: calc(-50% + 18px);
            height: 2px;
            border-radius: 2px;
            background: var(--border);
        }

        &.is-done {
            color: var(--brand);

            &::after {
                background: var(--brand-fill);
            }
        }

        &.is-current {
            color: var(--text-strong);
        }
    }
}

.od-journey.has-truck {
    padding-top: 26px;
    padding-bottom: 22px;
}

.od-journey-track {
    position: relative;

    // Espaço para o desenho da etapa respirar acima da trilha
    .has-truck & {
        padding-top: 66px;

        @media (max-width: 575px) {
            padding-top: 46px;
        }
    }
}

// Faixa do caminhão: termina no meio da linha da trilha (bolinha de 28px), para as rodas pisarem nela.
// As posições usam a mesma grade de 5 colunas: Enviado em 70%, Entregue em 90%.
.od-road {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 80px;
    pointer-events: none;
    // Acima da linha da trilha, que é desenhada depois
    z-index: 1;

    @media (max-width: 575px) {
        height: 60px;
    }
}

.od-truck {
    position: absolute;
    // O desenho tem uma folga abaixo das rodas (3 de 42 na altura): desconta para elas tocarem a linha
    bottom: -3.6px;
    width: 80px;
    transform: translateX(-50%);

    @media (max-width: 575px) {
        bottom: -1.5px;
        // No celular o trecho entre Enviado e Entregue mal cabe o caminhão: ele fica menor e não viaja
        width: 36px;
    }

    &.is-transit {
        left: 80%;
    }

    // Para logo antes da bolinha de Entregue, sem cobri-la
    &.is-delivered {
        left: calc(90% - 58px);
        animation: od-truck-drive 2.6s cubic-bezier(.45, 0, .2, 1) .7s both;

        @media (max-width: 575px) {
            left: calc(90% - 34px);
            animation: none;
        }
    }
}

.od-truck.is-delivered.from-transit {
    animation-name: od-truck-drive-from-transit;
    animation-delay: .15s;
}

@keyframes od-truck-drive-from-transit {
    0% { left: 80%; }
    88% { left: calc(90% - 56px); }
    100% { left: calc(90% - 58px); }
}

@keyframes od-truck-drive {
    0% { left: calc(70% + 58px); }
    88% { left: calc(90% - 56px); }
    100% { left: calc(90% - 58px); }
}


@media (prefers-reduced-motion: reduce) {
    .od-truck.is-delivered {
        animation: none;
    }
}

.od-stage-art {
    position: absolute;
    // Bem acima da bolinha atual (28px), com folga para não parecer espremido
    bottom: 30px;
    width: 46px;
    transform: translateX(-50%);
    animation: od-art-in .45s cubic-bezier(.22, 1, .36, 1) .35s both;

    @media (max-width: 575px) {
        bottom: 24px;
        width: 30px;
    }
}

.od-stage-art.is-billed {
    width: 44px;

    @media (max-width: 575px) {
        width: 30px;
    }
}

.od-stage-art.is-approved,
.od-stage-art.is-pendingAssignment {
    width: 56px;

    @media (max-width: 575px) {
        width: 36px;
    }
}

@keyframes od-art-in {
    from {
        opacity: 0;
        transform: translate(-50%, 6px);
    }
}

.od-sparkle {
    position: absolute;
    bottom: -1px;
    left: 90%;
    width: 0;
    height: 0;

    span {
        position: absolute;
        top: -1.5px;
        left: 0;
        width: 7px;
        height: 3px;
        border-radius: 2px;
        background: var(--brand-fill);
        opacity: 0;
        transform-origin: left center;
        animation: od-spark .7s cubic-bezier(.22, 1, .36, 1) both;
    }
}

// Cada raio sai do centro da bolinha, no seu ângulo, e some
@keyframes od-spark {
    0% { opacity: 0; transform: rotate(var(--a)) translateX(10px) scaleX(.4); }
    30% { opacity: 1; }
    100% { opacity: 0; transform: rotate(var(--a)) translateX(22px) scaleX(1); }
}

// Trilha se preenchendo ao abrir: cada trecho concluído cresce na sua vez e a bolinha acende
.od-journey li {
    &.is-done::after {
        transform-origin: left center;
        animation: od-trail-grow .3s ease-out both;
        animation-delay: calc(var(--i) * .15s + .2s);
    }

    .od-journey-dot {
        animation: od-dot-pop .35s cubic-bezier(.22, 1, .36, 1) both;
        animation-delay: calc(var(--i) * .15s + .1s);
    }

    &.is-current .od-journey-dot::after {
        content: '';
        position: absolute;
        top: -2px;
        right: -2px;
        bottom: -2px;
        left: -2px;
        border-radius: 50%;
        border: 2px solid var(--brand-soft);
        opacity: 0;
        animation: od-ring .9s ease-out both;
        animation-delay: calc(var(--i) * .15s + .45s);
    }
}

@keyframes od-trail-grow {
    from { transform: scaleX(0); }
}

@keyframes od-dot-pop {
    from { transform: scale(.6); opacity: .4; }
}

@keyframes od-ring {
    0% { opacity: .9; transform: scale(1); }
    100% { opacity: 0; transform: scale(1.9); }
}

@keyframes od-draw {
    from { stroke-dashoffset: 23; }
    to { stroke-dashoffset: 0; }
}

// Cancelado: etapas apagadas, linha tracejada e o marco vermelho no meio
.od-canceled-steps {
    display: grid !important;
    grid-template-columns: 1fr 1fr 2fr 1fr 1fr 1fr;
    align-items: start;

    li {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        color: var(--text-faint);
        font-size: 12px;
        font-weight: 700;
        text-align: center;

        &::after {
            display: none;
        }
    }
}

.od-journey-dot.is-off {
    background: var(--surface-1);
}

// A linha corre por trás de todas as bolinhas, do centro da primeira ao da última
.od-canceled-line {
    position: absolute;
    top: calc(66px + 13px);
    left: calc(100% / 14);
    right: calc(100% / 14);
    height: 2px;
    background: repeating-linear-gradient(to right, var(--border-strong) 0 6px, transparent 6px 11px);

    @media (max-width: 575px) {
        top: calc(46px + 13px);
    }
}

// Tom vermelho em volta do marco, sumindo para os lados
.od-canceled-tint {
    position: absolute;
    top: calc(66px + 13px);
    width: 26%;
    height: 2px;
    background: linear-gradient(to right, transparent, var(--danger-solid), transparent);
    transform: translateX(-50%);
    opacity: .7;

    @media (max-width: 575px) {
        top: calc(46px + 13px);
    }
}

.od-cancel-dot {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin-top: -1px;
    border-radius: 50%;
    background: var(--danger-solid);
    box-shadow: 0 0 0 5px color-mix(in srgb, var(--danger-solid) 22%, transparent);
    animation: od-dot-pop .4s cubic-bezier(.22, 1, .36, 1) .3s both;
}

.od-cancel-x {
    width: 18px;
    height: 18px;

    path {
        fill: none;
        stroke: var(--white);
        stroke-width: 2.6;
        stroke-linecap: round;
        stroke-dasharray: 23;
        animation: od-draw .4s ease-out .6s both;
    }
}

.od-cancel-title {
    color: var(--text-strong);
    font-family: $display;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: -.2px;

    @media (max-width: 575px) {
        font-size: 12px;
        line-height: 1.2;
    }
}

.od-cancel-text {
    max-width: 340px;
    margin-top: -2px;
    color: var(--text-muted);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;

    @media (max-width: 575px) {
        display: none;
    }
}

.od-stage-art.is-canceled {
    width: 56px;

    @media (max-width: 575px) {
        width: 36px;
    }
}

.od-alert.is-error .od-alert-icon {
    animation: od-nudge .5s ease-in-out .5s both;
}

@keyframes od-nudge {
    0%, 100% { transform: translateX(0) rotate(0); }
    20% { transform: translateX(-3px) rotate(-6deg); }
    45% { transform: translateX(3px) rotate(5deg); }
    70% { transform: translateX(-1.5px) rotate(-2deg); }
}

@media (prefers-reduced-motion: reduce) {
    .od-stage-art,
    .od-sparkle,
    .od-journey li.is-done::after,
    .od-journey li .od-journey-dot,
    .od-journey li.is-current .od-journey-dot::after,
    .od-cancel-x path,
    .od-cancel-dot,
    .od-alert.is-error .od-alert-icon {
        animation: none;
    }

    .od-sparkle {
        display: none;
    }
}

.od-journey-dot {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 2px solid var(--border-strong);
    border-radius: 50%;
    color: var(--text-faint);
    background: var(--surface-1);
    font-size: 12px;

    .is-done & {
        border-color: var(--brand-fill);
        color: var(--text-on-brand);
        background: var(--brand-fill);
    }

    .is-current & {
        border-color: var(--brand-solid);
        color: var(--text-on-brand);
        background: var(--brand-solid);
        box-shadow: 0 0 0 5px var(--focus-ring);
    }
}

.od-journey-label {
    @media (max-width: 575px) {
        font-size: 10px;
    }
}

.od-alert {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;
    padding: 16px 20px;
    border: 1px solid var(--danger-border);
    border-radius: 16px;
    color: var(--danger-fg);
    background: var(--danger-bg);

    > svg {
        margin-top: 3px;
        font-size: 18px;
    }

    p {
        margin: 0;
    }
}

.od-alert-title {
    font-size: 15px;
    font-weight: 800;
}

.od-alert-text {
    color: var(--text);
    font-size: 13px;
}

.od-items {
    margin-top: -4px;
}

// Resumo de valores no pé dos produtos, como um recibo
.od-receipt {
    display: grid;
    gap: 6px;
    margin: 6px 0 0 auto;
    padding-top: 14px;
    border-top: 1px dashed var(--border);
    max-width: 320px;

    div {
        display: flex;
        justify-content: space-between;
        gap: 16px;
    }

    dt {
        color: var(--text-muted);
        font-size: 13px;
        font-weight: 600;
    }

    dd {
        margin: 0;
        color: var(--text-strong);
        font-size: 14px;
        font-variant-numeric: tabular-nums;

        &.is-discount {
            color: var(--success-fg);
        }
    }

    .is-total {
        margin-top: 4px;
        padding-top: 10px;
        border-top: 1px solid var(--border-subtle);

        dt {
            color: var(--text-strong);
            font-size: 14px;
            font-weight: 800;
        }

        dd {
            color: var(--brand-strong);
            font-family: $display;
            font-size: 22px;
            font-weight: 800;
            letter-spacing: -.4px;
        }
    }
}

.od-invoice {
    & + & {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid var(--border-subtle);
    }
}

.od-key,
.od-id,
.od-tracking {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;

    code {
        color: var(--text-strong);
        font-family: $mono;
        font-size: 13px;
        word-break: break-all;
    }
}

.od-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 7px;
    color: var(--text-muted);
    background: transparent;
    font-size: 12px;
    cursor: pointer;
    transition: color .15s, background-color .15s, border-color .15s;

    &:hover,
    &:focus-visible {
        border-color: var(--tint-brand-border);
        color: var(--brand);
        background: var(--tint-brand-bg);
        outline: none;
    }
}

.od-invoice-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}

.od-notes p {
    margin: 0;
    color: var(--text);
    font-size: 14px;
    line-height: 1.55;

    & + p {
        margin-top: 10px;
    }
}

.od-person {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 12px;

    > span:last-child {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    strong {
        color: var(--text-strong);
        font-size: 15px;
        line-height: 1.3;
    }

    small {
        color: var(--text-muted);
        font-size: 12px;
    }
}

.od-avatar {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: var(--brand-strong);
    background: var(--tint-brand-bg);
    font-family: $display;
    font-size: 15px;
    font-weight: 800;
}

.od-contacts {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
        color: var(--text);
        font-size: 14px;
    }

    svg {
        flex-shrink: 0;
        width: 14px;
        color: var(--text-faint);
    }

    a {
        overflow: hidden;
        color: var(--brand);
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.od-address {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    color: var(--text);
    font-size: 14px;
    font-style: normal;
    line-height: 1.45;

    strong {
        margin-bottom: 2px;
        color: var(--text-strong);
    }
}

.od-reference {
    color: var(--text-muted);
    font-size: 13px;
}

.od-addr-block {
    & + & {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid var(--border-subtle);
    }
}

.od-addr-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    margin-bottom: 8px;

    .cpb {
        margin-left: auto;
    }
}

.od-addr-kind {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: var(--text-faint);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;

    svg {
        color: var(--brand);
        font-size: 12px;
    }
}

.od-cep {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    .cpb {
        width: 24px;
        min-width: 24px;
        height: 24px;
        margin-left: 2px;
        font-size: 11px;
    }
}

.od-cep-num {
    color: var(--text-strong);
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    letter-spacing: .02em;
}

// Cobrança = entrega: em vez de repetir o endereço, uma faixa que aponta para cima
.od-addr-same {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 10px 9px 12px;
    border: 1px dashed var(--tint-brand-border);
    border-radius: 10px;
    background: var(--tint-brand-bg);
}

.od-addr-same-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    color: var(--brand);
    background: var(--surface-1);
    box-shadow: 0 0 0 1px var(--tint-brand-border);
    font-size: 11px;
}

.od-addr-same-text {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    color: var(--text-muted);
    font-size: 12px;
    line-height: 1.35;

    strong {
        color: var(--text-strong);
        font-size: 13px;
    }
}

.od-addr-toggle {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 5px;
    height: 28px;
    padding: 0 10px;
    border: 0;
    border-radius: 7px;
    color: var(--brand);
    background: transparent;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color .15s;

    &:hover,
    &:focus-visible {
        background: var(--surface-1);
        outline: none;
    }

    &:focus-visible {
        box-shadow: 0 0 0 2px var(--tint-brand-border);
    }

    svg {
        font-size: 10px;
        transition: transform .2s cubic-bezier(.22, 1, .36, 1);

        &.is-open {
            transform: rotate(180deg);
        }
    }
}

.od-address.is-reveal {
    margin-top: 10px;
    padding-left: 12px;
    border-left: 2px solid var(--tint-brand-border);
    animation: od-addr-reveal .22s cubic-bezier(.22, 1, .36, 1);
}

@keyframes od-addr-reveal {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
    .od-address.is-reveal,
    .od-addr-toggle svg {
        animation: none;
        transition: none;
    }
}

.od-tracking-form {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 2px;
}

.od-input {
    flex: 1;
    min-width: 140px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--border-strong);
    border-radius: 9px;
    color: var(--text-strong);
    background: var(--surface-1);
    font-family: $mono;
    font-size: 13px;

    &:focus {
        border-color: var(--brand-soft);
        box-shadow: 0 0 0 3px var(--focus-ring);
        outline: none;
    }
}

.od-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--brand);
    font-weight: 700;

    svg {
        font-size: 10px;
    }
}


.od-payment {
    & + & {
        margin-top: 14px;
        padding-top: 14px;
        border-top: 1px solid var(--border-subtle);
    }
}

.od-payment-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;

    strong {
        flex: 1;
        min-width: 0;
        color: var(--text-strong);
        font-size: 14px;
    }
}

.od-payment-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    color: var(--brand);
    background: var(--tint-brand-bg);
    font-size: 13px;
}

.od-payment-value {
    color: var(--text-strong);
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    font-weight: 800;
}

.od-payment-facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 14px;
    margin: 10px 0 0 40px;

    .is-wide {
        grid-column: 1 / -1;
    }

    dt {
        color: var(--text-faint);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .1em;
        text-transform: uppercase;
    }

    dd {
        margin: 0;
        color: var(--text);
        font-size: 13px;

        code {
            color: var(--text-strong);
            font-family: $mono;
        }
    }
}

.od-channel {
    margin-bottom: 14px;
    font-weight: 700;
}
</style>
